import { FC, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import ClickableButton from '@/components/Buttons/ClickableButton';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import HubspotForm from '@/components/Modal/HubspotForm';
import HubspotFormModal from '@/components/Modal/HubspotFormModal';
import { HUBSPOT_FORM_IDS } from '@/lib/constants';
import { parseHTML } from '@/lib/parse';
import { GlobalContext } from '@/pages/_app';

import DesktopSlider from './DesktopSlider';
import MobileSlider from './MobileSlider';

const checkIfNumberInRange = (x: number, min: number, max: number) =>
	x >= min && x <= max;

const ScrollPhoneSlider: FC<any> = ({
	cta,
	title,
	slides,
	description,
	buttonImage,
	PhoneSlider,
}) => {
	const { device, lang } = useContext(GlobalContext);
	const [activeSlide, setActiveSlide] = useState(1);
	const phoneRef = useRef<any>(null);
	const wrapperRef = useRef<any>(null);
	const [currentFormId, setCurrentFormId] = useState<string | null>(null);
	const leftSlides = slides?.filter((_, index) => index % 2 === 0);
	const rightSlides = slides?.filter((_, index) => index % 2 === 1);

	const openModal = (formId) => setCurrentFormId(formId);

	const closeModal = () => setCurrentFormId(null);

	useEffect(() => {
		if (!phoneRef.current || !wrapperRef.current) return;

		wrapperRef.current.style.overflow = 'visible';

		const slides = Array.from(document.querySelectorAll(`[data-slide]`));

		const intersectionThreshold = lang === 'en' ? 200 : 180;

		const scrollListener = () => {
			const phoneRect = phoneRef.current.getBoundingClientRect();
			const halfOfPhoneHeight = phoneRect.height / 2;

			const phoneRectIntersectionPoint = phoneRect.top + halfOfPhoneHeight;

			slides?.forEach((slide) => {
				const slideRect = slide.getBoundingClientRect();
				const halfOfSlideHeight = slideRect.height / 2;
				const slideIntersectionPoint = slideRect.top + halfOfSlideHeight;
				const doesOverlap = checkIfNumberInRange(
					phoneRectIntersectionPoint,
					slideIntersectionPoint - intersectionThreshold,
					slideIntersectionPoint + intersectionThreshold,
				);

				if (doesOverlap) {
					const slideId = Number.parseInt(
						slide?.getAttribute('data-slide-id') ?? '',
					);

					setActiveSlide(slideId);
				}
			});
		};

		window.addEventListener('scroll', scrollListener);

		return () => window.removeEventListener('scroll', scrollListener);
	}, [phoneRef.current, wrapperRef.current]);

	return (
		<Wrapper>
			<Title>{title ? parseHTML(title) : null}</Title>

			<Description>{description}</Description>

			{cta ? (
				<BotButtonWrapper>
					{cta?.link?.includes('hsforms') ? (
						<ClickableButton
							width="wide"
							text={cta?.text}
							onClick={() => openModal(HUBSPOT_FORM_IDS.MAIN)}
						/>
					) : (
						<PrimaryButton text={cta?.text} href={cta?.link} />
					)}

					{buttonImage ? (
						<BotGuy
							style={{
								backgroundImage: `url('${buttonImage}')`,
							}}
						/>
					) : null}
				</BotButtonWrapper>
			) : null}

			{device === 'mobile' ? (
				<MobileSlider
					slides={slides}
					phoneRef={phoneRef}
					activeSlide={activeSlide}
					PhoneSlider={PhoneSlider}
					onHeadingClick={setActiveSlide}
				/>
			) : (
				<DesktopSlider
					phoneRef={phoneRef}
					wrapperRef={wrapperRef}
					activeSlide={activeSlide}
					leftSlides={leftSlides}
					rightSlides={rightSlides}
					PhoneSlider={PhoneSlider}
				/>
			)}

			{currentFormId ? (
				<HubspotFormModal handleClose={closeModal}>
					<HubspotForm formId={currentFormId} handleClose={closeModal} />
				</HubspotFormModal>
			) : null}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	padding: 120px 24px 120px;
	width: 1248px;
	max-width: 100%;
	margin: 0 auto;
	position: relative;

	@media (max-width: 800px) {
		padding: 25px 15px 50px;
	}
`;

const Title = styled.h2`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	color: var(--color-purple);
	text-align: center;
	max-width: 100%;
	margin: 0 auto 20px;

	@media (max-width: 800px) {
		width: 90%;
		font-size: 40px;
		margin-bottom: 15px;
		padding: 0 15px;
	}
`;

const Description = styled.div`
	font-family: var(--font-sans);
	color: var(--color-black);
	font-size: 18px;
	line-height: 25px;
	margin: 0 auto 35px;
	width: 58%;
	max-width: 100%;
	text-align: center;

	@media (max-width: 800px) {
		width: 100%;
		font-size: 16px;
		margin-bottom: 20px;
		padding: 0 15px;
	}
`;

const BotButtonWrapper = styled.div`
	position: sticky;
	top: 550px;
	margin-left: auto;
	width: max-content;
	z-index: 2;

	@media (max-width: 1240px) {
		position: relative;
		top: 0;
		left: 0;
		margin: 0 auto;
		width: 195px;
		height: 180px;
		padding-top: 110px;
	}

	@media (max-width: 500px) {
		height: 140px;
		padding-top: 80px;
		width: 100%;
	}
`;

const BotGuy = styled.div`
	position: absolute;
	top: -95px;
	left: 15px;
	width: 160px;
	height: 180px;
	background-repeat: no-repeat;
	background-position: center;
	background-size: contain;

	@media (max-width: 1240px) {
		top: 15px;
	}
`;

export default ScrollPhoneSlider;
